import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { proXpApi, formatCents } from "@/lib/experienceApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, BarChart3, Percent } from "lucide-react";
import { auth } from "@/lib/api";

export default function AdminExperience() {
  const [orgs, setOrgs] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [commission, setCommission] = useState(null);
  const [pct, setPct] = useState(500);

  useEffect(() => {
    const u = auth.getUser();
    if (!u || u.role !== "admin") { toast.error("Admin uniquement"); return; }
    proXpApi.adminAllOrgs().then(setOrgs);
    proXpApi.adminGlobalKPIs().then(setKpis);
    proXpApi.adminGetGlobalCommission().then((c) => { setCommission(c); setPct(c?.default_commission_pct || 500); });
  }, []);

  const saveCommission = async () => {
    try {
      await proXpApi.adminSetGlobalCommission({ default_commission_pct: pct });
      toast.success("Commission mise à jour");
    } catch { toast.error("Impossible"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-3xl font-display text-[#1F3A5F] mb-1">Admin geoVINUM Experience</h1>
      <p className="text-sm text-neutral-600 mb-5">Vue multi-organisations · commissions · KPIs globaux.</p>

      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Stat label="Organisations" value={kpis.orgs} />
          <Stat label="Événements" value={kpis.events} />
          <Stat label="GMV" value={formatCents(kpis.gmv_cents)} />
          <Stat label="Commission" value={formatCents(kpis.commission_cents)} />
        </div>
      )}

      <Card className="mb-4"><CardContent className="p-4">
        <div className="text-sm font-semibold flex items-center gap-2"><Percent className="w-4 h-4" /> Commission par défaut</div>
        <div className="text-xs text-neutral-500 mb-2">En basis points (100 bp = 1%). S'applique aux nouvelles organisations.</div>
        <div className="flex gap-2 items-center">
          <Input data-testid="global-commission-input" type="number" value={pct} onChange={(e) => setPct(parseInt(e.target.value) || 0)} className="w-32" />
          <span className="text-sm">bp ({(pct / 100).toFixed(1)}%)</span>
          <Button size="sm" data-testid="save-commission" onClick={saveCommission} className="bg-[#E5276A] hover:bg-[#C01661]">Enregistrer</Button>
        </div>
      </CardContent></Card>

      <h2 className="text-lg font-semibold mb-3">Organisations</h2>
      <div className="grid gap-2">
        {orgs.map(o => (
          <Card key={o.id}><CardContent className="p-3 flex items-center justify-between text-sm">
            <div>
              <div className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-[#E5276A]" /> {o.name}</div>
              <div className="text-xs text-neutral-500">Owner: {o.owner_user_id.slice(0, 8)}… · Stripe: {o.stripe_account_status}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-neutral-500">{(o.default_commission_pct / 100).toFixed(1)}%</span>
              <OrgCommissionInline id={o.id} initial={o.default_commission_pct} onSaved={() => proXpApi.adminAllOrgs().then(setOrgs)} />
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <Card><CardContent className="p-3">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="text-lg font-bold text-[#E5276A] mt-1">{value}</div>
    </CardContent></Card>
  );
}

function OrgCommissionInline({ id, initial, onSaved }) {
  const [v, setV] = useState(initial);
  const [editing, setEditing] = useState(false);
  const save = async () => {
    try { await proXpApi.adminSetOrgCommission(id, { default_commission_pct: v }); toast.success("OK"); setEditing(false); onSaved(); }
    catch { toast.error("Impossible"); }
  };
  if (!editing) return <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Modifier</Button>;
  return (
    <div className="flex gap-1">
      <Input type="number" value={v} onChange={(e) => setV(parseInt(e.target.value) || 0)} className="w-20 text-xs h-7" />
      <Button size="sm" onClick={save} className="bg-[#E5276A] hover:bg-[#C01661] h-7">OK</Button>
    </div>
  );
}
